import { Button, Row, InfoSheetPanel } from '@fs/zion-ui'
import React, { useCallback, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSlsHighlightsContext, useSlsStateContext } from '@fs/zion-sls'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import HighlightPanelContent from './HighlightPanelContent'

/**
 * HighlightPanel component for managing and editing image highlights.
 * Opens itself on mount, preserving the animation effect while keeping unnecessary stuff out of the document while not in use.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} [props.splitScreenImageArk] - Optional image identifier for split-screen mode.
 * @param {Function} [props.onSave] - Callback invoked after highlights are saved.
 * @param {Function} [props.onCancel] - Callback invoked when the panel is cancelled or closed.
 *
 * @returns {JSX.Element} The rendered highlight panel component.
 */
const HighlightPanel = ({ splitScreenImageArk, onSave: onSaveProp, onCancel: onCancelProp }) => {
  const [t] = useTranslation()
  const { highlightsPanelOverlay, closeOverlay, openOverlay } = useOverlayContext()
  const { types } = useSlsStateContext()
  const {
    setCreatingRects,
    setEditMode,
    stageEdits,
    setActiveFieldInfo,
    activeFieldInfo,
    // onSave: onSaveFromHook,
    clearDisplay,
    getHighlights,
    clearEdits,
  } = useSlsHighlightsContext()

  const isEditing = useMemo(
    () => getHighlights().mainEditHighlights.find((h) => h.fieldId === activeFieldInfo.id),
    [getHighlights, activeFieldInfo.id]
  )

  useEffect(() => {
    openOverlay('highlightsPanel')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Open overlay as soon as component mounts

  const resetSlsHighlightState = useCallback(
    (resetActiveFieldInfo = true) => {
      setCreatingRects(false)
      setEditMode(false)
      if (resetActiveFieldInfo) setActiveFieldInfo({ id: null, value: null })
    },
    [setCreatingRects, setEditMode, setActiveFieldInfo]
  )

  const title = isEditing
    ? t('editHighlight.panelHeader.panelHeadingEdit', 'Edit Highlight')
    : t('editHighlight.panelHeader.panelHeadingAdd', 'Add Highlight')

  const handleCancel = useCallback(() => {
    clearEdits()
    clearDisplay({ all: true })
    resetSlsHighlightState()
    closeOverlay('highlightsPanel')
    onCancelProp?.()
  }, [resetSlsHighlightState, closeOverlay, clearDisplay, onCancelProp, clearEdits])

  const handleSave = useCallback(() => {
    // Helper function to filter attached highlights
    const getAttachedHighlights = (highlights) => highlights?.filter((h) => h?.attached === true)

    const { mainEditHighlights, splitScreenEditHighlights } = stageEdits()
    const mainSelectedHighlights = getAttachedHighlights(mainEditHighlights)
    const splitScreenSelectedHighlights = getAttachedHighlights(splitScreenEditHighlights)
    const fieldType = types?.fields.find((type) => type.fieldType === activeFieldInfo?.fieldType)

    // Disable running onSave from hook for the fullTextAttachFlow v1 release.
    // This is the connection between the HighlightPanel and the SharedHighlight flow, which we will need for the PersonOverviewV2 release
    // if (onSaveFromHook) {
    //   // Running the onSave function from the hook so it can handle things like checking for shared highlights
    //   onSaveFromHook({
    //     mainEditHighlights,
    //     splitScreenEditHighlights,
    //     mainSelectedHighlights,
    //     splitScreenSelectedHighlights,
    //     fieldType,
    //     activeFieldInfo,
    //   })
    //   resetSlsHighlightState(false)
    //   onSaveProp?.({
    //     mainEditHighlights,
    //     splitScreenEditHighlights,
    //     mainSelectedHighlights,
    //     splitScreenSelectedHighlights,
    //     fieldType,
    //     activeFieldInfo,
    //   }) // Notify parent component if needed
    // } else {
    resetSlsHighlightState()
    closeOverlay('highlightsPanel')
    onSaveProp?.({
      mainEditHighlights,
      splitScreenEditHighlights,
      mainSelectedHighlights,
      splitScreenSelectedHighlights,
      fieldType,
      activeFieldInfo,
    }) // Notify parent component if needed
    // }
  }, [stageEdits, types, closeOverlay, activeFieldInfo, resetSlsHighlightState, onSaveProp])

  return (
    <InfoSheetPanel
      headingColor="yellow"
      title={title}
      {...highlightsPanelOverlay}
      close={handleCancel}
      footer={
        <Row alignX="end">
          <Button onClick={handleCancel} linkName="Edit Highlights Cancel" data-testid="highlights-panel-cancel-button">
            {t('edit-index.cancel.button', 'Cancel')}
          </Button>
          <Button
            emphasis="high"
            onClick={handleSave}
            linkName="Edit Highlights Save"
            data-testid="highlights-panel-save-button"
          >
            {t('edit-index.update.button', 'Update')}
          </Button>
        </Row>
      }
    >
      <HighlightPanelContent splitScreenImageArk={splitScreenImageArk} title={title} onSave={onSaveProp} />
    </InfoSheetPanel>
  )
}

export default HighlightPanel
